/**
 * OffenceRepository - Database operations for offences and categories collections
 */

import { BaseRepository, where } from './BaseRepository';

export class OffenceRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - offences are now stored at schools/{schoolId}/offences/{offenceId}
    super('offences', { isHierarchical: true });
    this.categoriesCollection = 'disciplineCategories';
    // CRITICAL: Categories are also hierarchical - schools/{schoolId}/disciplineCategories/{categoryId}
    this.categoriesRepo = new BaseRepository('disciplineCategories', { isHierarchical: true });
  }

  /**
   * Get all offences sorted by category and name
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of offences
   */
  async getAllOffences(schoolId) {
    // Guard: require schoolId
    if (!schoolId) {
      console.warn('OffenceRepository.getAllOffences: schoolId required but not provided');
      return [];
    }

    // Query hierarchical path - schoolId is used for routing, not filtering
    const constraints = [];

    const docs = await this.queryDocuments(constraints, schoolId);

    // Sort by category then name on client side
    return docs.sort((a, b) => {
      const categoryCompare = (a.categoryId || '').localeCompare(b.categoryId || '');
      if (categoryCompare !== 0) return categoryCompare;
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  /**
   * Get offences by category
   * @param {string} categoryId - Category ID
   * @returns {Promise<Array>} Array of offences in category
   */
  async getByCategory(categoryId) {
    const constraints = [
      where('categoryId', '==', categoryId),
      orderBy('name', 'asc')
    ];

    return this.queryDocuments(constraints);
  }

  /**
   * Get offence by name
   * @param {string} name - Offence name
   * @returns {Promise<Object|null>} Offence object or null
   */
  async getByName(name, schoolId = null) {
    const constraints = [
      where('name', '==', name)
    ];

    const results = await this.queryDocuments(constraints, schoolId);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Create new offence
   * @param {Object} offenceData - Offence data
   * @returns {Promise<Object>} Created offence
   */
  async createOffence(offenceData) {
    // Ensure category exists
    if (offenceData.categoryId) {
      const categoryExists = await this.categoriesRepo.exists(offenceData.categoryId, offenceData.schoolId);
      if (!categoryExists) {
        throw new Error(`Category ${offenceData.categoryId} does not exist`);
      }
    }

    return this.create({
      ...offenceData,
      active: offenceData.active !== false, // Default to active
      level: offenceData.level || 1 // Default level
    });
  }

  /**
   * Update offence
   * @param {string} offenceId - Offence ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated offence
   */
  async updateOffence(offenceId, updates) {
    // If updating category, ensure it exists
    if (updates.categoryId) {
      const categoryExists = await this.categoriesRepo.exists(updates.categoryId, updates.schoolId);
      if (!categoryExists) {
        throw new Error(`Category ${updates.categoryId} does not exist`);
      }
    }

    return this.update(offenceId, updates);
  }

  /**
   * Get active offences
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of active offences
   */
  async getActiveOffences(schoolId) {
    // Guard: require schoolId
    if (!schoolId) {
      console.warn('OffenceRepository.getActiveOffences: schoolId required but not provided');
      return [];
    }

    // Query hierarchical path - schoolId routes to correct collection
    const constraints = [
      where('active', '==', true)
    ];

    const docs = await this.queryDocuments(constraints, schoolId);

    // Sort by category then name on client side
    return docs.sort((a, b) => {
      const categoryCompare = (a.categoryId || '').localeCompare(b.categoryId || '');
      if (categoryCompare !== 0) return categoryCompare;
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  /**
   * Deactivate offence
   * @param {string} offenceId - Offence ID
   * @returns {Promise<Object>} Updated offence
   */
  async deactivateOffence(offenceId) {
    return this.update(offenceId, {
      active: false,
      deactivatedAt: Timestamp.now()
    });
  }

  // Category management methods

  /**
   * Get all categories
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of categories
   */
  async getAllCategories(schoolId) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('OffenceRepository.getAllCategories: schoolId required but not provided');
      return [];
    }

    // Simplified query to avoid index requirements
    const constraints = [
      orderBy('name', 'asc')
    ];

    const docs = await this.categoriesRepo.queryDocuments(constraints, schoolId);

    // Sort by order then name on client side
    return docs.sort((a, b) => {
      const orderCompare = (a.order || 0) - (b.order || 0);
      if (orderCompare !== 0) return orderCompare;
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  /**
   * Get category by ID
   * @param {string} categoryId - Category ID
   * @returns {Promise<Object|null>} Category object or null
   */
  async getCategoryById(categoryId) {
    return this.categoriesRepo.getById(categoryId);
  }

  /**
   * Get category by name
   * @param {string} name - Category name
   * @returns {Promise<Object|null>} Category object or null
   */
  async getCategoryByName(name) {
    const constraints = [
      where('name', '==', name)
    ];

    const results = await this.categoriesRepo.queryDocuments(constraints);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Create new category
   * @param {Object} categoryData - Category data
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Created category
   */
  async createCategory(categoryData, schoolId) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('OffenceRepository.createCategory: schoolId required but not provided');
      throw new Error('schoolId is required for category creation');
    }

    // Get max order to place new category at the end
    const categories = await this.getAllCategories(schoolId);
    const maxOrder = categories.reduce((max, cat) =>
      Math.max(max, cat.order || 0), 0
    );

    return this.categoriesRepo.create({
      ...categoryData,
      order: categoryData.order !== undefined ? categoryData.order : maxOrder + 1,
      active: categoryData.active !== false // Default to active
    });
  }

  /**
   * Update category
   * @param {string} categoryId - Category ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated category
   */
  async updateCategory(categoryId, updates) {
    return this.categoriesRepo.update(categoryId, updates);
  }

  /**
   * Delete category (only if no offences)
   * @param {string} categoryId - Category ID
   * @returns {Promise<void>}
   */
  async deleteCategory(categoryId) {
    // Check if any offences use this category
    const offencesInCategory = await this.getByCategory(categoryId);

    if (offencesInCategory.length > 0) {
      throw new Error(`Cannot delete category with ${offencesInCategory.length} offences`);
    }

    return this.categoriesRepo.delete(categoryId);
  }

  /**
   * Reorder categories
   * @param {Array} categoryIds - Array of category IDs in new order
   * @returns {Promise<Array>} Updated categories
   */
  async reorderCategories(categoryIds) {
    const promises = categoryIds.map((id, index) =>
      this.categoriesRepo.update(id, { order: index })
    );

    return Promise.all(promises);
  }

  /**
   * Get offence with category details
   * @param {string} offenceId - Offence ID
   * @returns {Promise<Object|null>} Offence with category details
   */
  async getOffenceWithCategory(offenceId) {
    const offence = await this.getById(offenceId);

    if (!offence) {
      return null;
    }

    if (offence.categoryId) {
      const category = await this.getCategoryById(offence.categoryId);
      offence.category = category;
    }

    return offence;
  }

  /**
   * Get all offences grouped by category
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Offences grouped by category
   */
  async getOffencesGroupedByCategory(schoolId) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('OffenceRepository.getOffencesGroupedByCategory: schoolId required but not provided');
      return {};
    }

    const [offences, categories] = await Promise.all([
      this.getAllOffences(schoolId),
      this.getAllCategories(schoolId)
    ]);

    const grouped = {};

    // Initialize categories
    categories.forEach(category => {
      grouped[category.id] = {
        ...category,
        offences: []
      };
    });

    // Add uncategorized group
    grouped.uncategorized = {
      id: 'uncategorized',
      name: 'Uncategorized',
      offences: []
    };

    // Group offences
    offences.forEach(offence => {
      const categoryId = offence.categoryId || 'uncategorized';
      if (grouped[categoryId]) {
        grouped[categoryId].offences.push(offence);
      }
    });

    return grouped;
  }

  /**
   * Search offences by partial name
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Array of matching offences
   */
  async searchOffences(searchTerm) {
    // Note: Firestore doesn't support full-text search
    // This will need to be done client-side or with a search service
    const allOffences = await this.getAllOffences();

    const searchLower = searchTerm.toLowerCase();
    return allOffences.filter(offence =>
      offence.name.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Get offence statistics
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Statistics object
   */
  async getOffenceStatistics(schoolId) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('OffenceRepository.getOffenceStatistics: schoolId required but not provided');
      return {};
    }

    const [offences, categories] = await Promise.all([
      this.getAllOffences(schoolId),
      this.getAllCategories(schoolId)
    ]);

    return {
      totalOffences: offences.length,
      activeOffences: offences.filter(o => o.active !== false).length,
      totalCategories: categories.length,
      offencesByLevel: offences.reduce((acc, offence) => {
        const level = offence.level || 1;
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      }, {})
    };
  }
}